import { useCallback, useContext, useMemo, useState } from 'react'
import { Keyboard, Pressable, ScrollView, Text, useWindowDimensions, View } from 'react-native'
import {
  formatLocalFiatAmount,
  formatMarketPrice,
  formatTokenAmount,
  marketPresentation
} from '@/config/marketPresentation'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { Image } from 'expo-image'
import { router, useFocusEffect, useRouter } from 'expo-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { MAX_DEAL_AMOUNT, PaymentKind, UserFlags } from '@/constants/common'
import { CREATE_BUY_ORDER, CREATE_JBP_DEPOSIT_INVOICE } from '@/constants/graphql/mutations'
import {
  GET_CURRENT_VIP_RECORD,
  GET_JBP_PAYMENT_METHOD_SETTINGS,
  GET_USER,
  RECOMMEND_BUY_AMOUNT
} from '@/constants/graphql/queries'
import { CurrencyKind } from '@/types/wallet'
import { formatDate } from '@/utils/formatter'
import { getGraphQlErrorCode, getTradingDisabledMessage, isFunctionDisabledError } from '@/utils/graphqlError'
import {
  buildJbpIdempotencyKey,
  calculateJbpNetCredit,
  CurrentVipRecordQuery,
  formatJbpCurrency,
  formatJbpFeeLabel,
  formatJbpFeeSnapshotLabel,
  formatJbpRangeLabel,
  getEligibleJbpSettings,
  getJbpPaymentMethodLabel,
  JBP_DEPOSITS_ENABLED,
  JbpDepositInvoice,
  JbpPaymentMethodSetting,
  JbpPaymentMethodSettingsQuery
} from '@/utils/jbp'
import { toCurrency } from '@/utils/numbers'
import { maxKYC1Flag } from '@/utils/user'
import { AuthContext } from '@/stores/auth'
import {
  Banner,
  Button,
  FormScrollView,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  useModal,
  useToast
} from '@/components/base'
import DescriptionListItem from '@/components/DescriptionListItem'
import { NumberInput } from '@/components/form-control'
import { Close } from '@/components/icon'
import JbpCheckout from '@/components/jbp/JbpCheckout'
import { JbpVipConditions } from '@/components/jbp/JbpVipConditions'
import { useLoadingIndicator } from '@/components/LoadingIndicator'
import { SelectPaymentMethodContext, SelectPaymentMethodTrigger } from '@/components/SelectPaymentMethodScreen'

const BASE_PRICE = 1
const defaultValues: BuyValues = {
  dealAmount: 0
}

type CurrentPayment = {
  id: string
  kind: PaymentKind
}

type CreateJbpDepositInvoiceMutation = {
  createJbpDepositInvoice: {
    outcome: 'PAYMENT_INSTRUCTIONS_READY' | 'PAYMENT_METHOD_UNAVAILABLE'
    invoice?: JbpDepositInvoice | null
    unavailablePaymentMethod?: JbpPaymentMethodSetting['method'] | null
  }
}

export type BuyValues = {
  dealAmount: number
  selectedPayment?: CurrentPayment
}

export default function BuyScreen() {
  const { t } = useTranslation()
  const auth = AuthContext.useActorRef()
  const loading = useLoadingIndicator()
  const {
    selectedBuyPaymentText: selectedPaymentText,
    setSelectedBuyPaymentText: setSelectedPaymentText,
    selectedBuyPayment: selectedPayment,
    setSelectedBuyPayment: setSelectedPayment,
    open,
    setType,
    isHidden,
    show
  } = useContext(SelectPaymentMethodContext)
  const { control, handleSubmit, reset, watch, setError } = useForm<BuyValues>({ defaultValues })
  const toast = useToast()
  const [submittedValues, setSubmittedValues] = useState<BuyValues | undefined>()
  const [preferStandardC2c, setPreferStandardC2c] = useState(false)
  const { openModal, closeModal } = useModal()
  const user = AuthContext.useSelector((state) => state.context.values?.user)

  const onCloseModalAndResetForm = () => {
    reset()
    setPreferStandardC2c(false)
    setSelectedPayment(undefined)
    setSelectedPaymentText('')
    closeModal()
  }

  const [maxKYC, setMaxKYC] = useState<UserFlags>(UserFlags.EMPTY)

  const { refetch: refetchUser } = useQuery(GET_USER, {
    variables: {
      id: user?.id
    },
    onCompleted: (data) => {
      const userFlags = data.user?.flags
      const maxFlag = userFlags ? maxKYC1Flag(userFlags) : UserFlags.EMPTY
      setMaxKYC(maxFlag)
    }
  })

  const { data: currentVipRecordData } = useQuery<CurrentVipRecordQuery>(GET_CURRENT_VIP_RECORD)
  const {
    data: jbpPaymentSettingsData,
    loading: jbpPaymentSettingsLoading,
    refetch: refetchJbpPaymentSettings
  } = useQuery<JbpPaymentMethodSettingsQuery>(GET_JBP_PAYMENT_METHOD_SETTINGS, { skip: !JBP_DEPOSITS_ENABLED })

  const shownDealAmount = watch('dealAmount') // / BASE_PRICE
  const currentVipLevel = currentVipRecordData?.getCurrentVipRecord?.level ?? 0
  const jbpPaymentMethodSettings = jbpPaymentSettingsData?.getJbpPaymentMethodSettings?.data ?? []
  const eligibleJbpSettings = useMemo(
    () =>
      JBP_DEPOSITS_ENABLED
        ? getEligibleJbpSettings(jbpPaymentMethodSettings, Number(shownDealAmount || 0), currentVipLevel)
        : [],
    [currentVipLevel, jbpPaymentMethodSettings, shownDealAmount]
  )

  const [createBuyOrder] = useMutation(CREATE_BUY_ORDER, {
    onCompleted: (data) => {
      loading.stop()
      reset()
      setPreferStandardC2c(false)
      setSelectedPayment(undefined)
      setSelectedPaymentText('')

      // Navigate to the order details screen
      router.push(`/order/buy/${data.createBuyOrder.internalKey}`)
    },
    onError: (error) => {
      loading.stop()
      const errorCode = getGraphQlErrorCode(error)
      if (errorCode === 3008) {
        openModal({
          children: <TooManyOrdersModal onClose={onCloseModalAndResetForm} />,
          onClose: closeModal
        })
      } else if (errorCode === 3018) {
        toast.show({ variant: 'error', description: t('c2c:paymentMethodUnavailable') })
      } else if (isFunctionDisabledError(error)) {
        toast.show({ variant: 'error', description: getTradingDisabledMessage() })
      } else {
        toast.show({ variant: 'error', description: t('c2c:serverError') })
      }
    }
  })

  const [createJbpDepositInvoice] = useMutation<CreateJbpDepositInvoiceMutation>(CREATE_JBP_DEPOSIT_INVOICE)

  const [recommendBuyAmount] = useLazyQuery(RECOMMEND_BUY_AMOUNT, {
    onCompleted: (data) => {
      loading.stop()
      //If everything is fine, create the buy order
      if (submittedValues?.dealAmount.toString() === data.recommendBuyAmount.amount) {
        loading.start()
        createBuyOrder({
          variables: {
            amount: {
              kind: CurrencyKind[CurrencyKind.YBB],
              value: submittedValues?.dealAmount.toString()
            },
            paymentMethodId: submittedValues?.selectedPayment?.id
          }
        })
      } else {
        openModal({
          className: 'p-4',
          children: (
            <>
              <Text className={'text-base font-medium text-text-primary'}>{t('c2c:tradeRecommendation')}</Text>
              <Pressable
                className={'absolute right-2 top-2 size-10 items-center justify-center'}
                onPress={onCloseModalAndResetForm}
              >
                <Close className={'text-text-tertiary'} />
              </Pressable>
              <View className={'h-6'} />
              <Text className={'text-sm text-text-secondary'}>{t('c2c:tradeRecommendationDescription')}</Text>
              <View className={'h-[22px]'} />
              <View className={'flex-row justify-between'}>
                <Text className={'text-sm text-text-secondary'}>{t('orders:paymentAmount')}</Text>
                <Text className={'font-roboto-medium text-sm text-text-primary'}>
                  {formatLocalFiatAmount(data.recommendBuyAmount.amount)}
                </Text>
              </View>
              <View className={'h-7'} />
              <View className={'flex-row justify-between'}>
                <Text className={'text-sm text-text-secondary'}>{t('orders:price')}</Text>
                <Text className={'font-roboto-medium text-sm font-medium text-text-primary'}>
                  {formatMarketPrice(BASE_PRICE)}
                </Text>
              </View>
              <View className={'h-7'} />
              <View className={'flex-row justify-between'}>
                <Text className={'text-sm text-text-secondary'}>{t('orders:expectedReceived')}</Text>
                <Text className={'font-roboto-medium text-sm font-medium text-text-primary'}>
                  {formatTokenAmount(data.recommendBuyAmount.amount)}
                </Text>
              </View>
              <View className={'h-[30px]'} />
              <Button
                variant={'solid'}
                intent={'primary'}
                size={'md'}
                onPress={() => {
                  closeModal()
                  loading.start()
                  createBuyOrder({
                    variables: {
                      amount: {
                        kind: CurrencyKind[CurrencyKind.YBB],
                        value: data.recommendBuyAmount.amount
                      },
                      paymentMethodId: submittedValues?.selectedPayment?.id
                    }
                  })
                }}
              >
                {t('c2c:tradeWithRecommendation')}
              </Button>
              <View className={'h-3'} />
              <Button
                variant={'outline'}
                intent={'primary'}
                size={'md'}
                onPress={() => {
                  closeModal()
                  loading.start()
                  createBuyOrder({
                    variables: {
                      amount: {
                        kind: CurrencyKind[CurrencyKind.YBB],
                        value: submittedValues?.dealAmount.toString()
                      },
                      paymentMethodId: submittedValues?.selectedPayment?.id
                    }
                  })
                }}
              >
                {t('c2c:listOriginalAmount')}
              </Button>
            </>
          ),
          onClose: closeModal
        })
      }
    },
    onError: (error) => {
      loading.stop()
      const errorCode = getGraphQlErrorCode(error)
      if (errorCode === 2015) {
        openModal({
          children: <TooManyOrdersModal onClose={onCloseModalAndResetForm} />,
          onClose: closeModal
        })
      } else if (isFunctionDisabledError(error)) {
        toast.show({ variant: 'error', description: getTradingDisabledMessage() })
      } else {
        toast.show({ variant: 'error', description: t('c2c:serverError') })
      }
    }
  })

  const startC2cBuyOrder = (values: BuyValues) => {
    if (!selectedPayment) {
      toast.show({ variant: 'error', description: t('c2c:selectPaymentMethod') })
      return
    }

    setSubmittedValues({ ...values, selectedPayment })
    loading.start()
    recommendBuyAmount({
      variables: {
        amount: {
          kind: CurrencyKind[CurrencyKind.YBB],
          value: values.dealAmount.toString()
        },
        paymentMethodId: selectedPayment.id
      }
    })
  }

  const openJbpOptions = (
    values: BuyValues,
    unavailableMethods: JbpPaymentMethodSetting['method'][] = [],
    settings = jbpPaymentMethodSettings,
    failureMessage?: string
  ) => {
    openModal({
      children: (
        <ExpressTradingOptionsModal
          amount={values.dealAmount}
          failureMessage={failureMessage}
          onClose={closeModal}
          onUseC2c={() => {
            closeModal()
            if (selectedPayment) {
              startC2cBuyOrder(values)
            } else {
              reset(values)
              setPreferStandardC2c(true)
              onSelectPaymentMethod()
            }
          }}
          onUseJbp={(nextSetting) => startJbpBuyOrder(values, nextSetting, unavailableMethods)}
          settings={getEligibleJbpSettings(settings, values.dealAmount, currentVipLevel)}
          unavailableMethods={Array.from(new Set(unavailableMethods))}
          vipLevel={currentVipLevel}
        />
      ),
      onClose: closeModal
    })
  }

  const startJbpBuyOrder = async (
    values: BuyValues,
    setting: JbpPaymentMethodSetting,
    knownUnavailableMethods: JbpPaymentMethodSetting['method'][] = []
  ) => {
    setPreferStandardC2c(false)
    loading.start()
    try {
      const result = await createJbpDepositInvoice({
        variables: {
          input: {
            amount: values.dealAmount.toString(),
            bankId: setting.bankId,
            idempotencyKey: buildJbpIdempotencyKey('mobile-c2c-buy')
          }
        }
      })
      const creationResult = result.data?.createJbpDepositInvoice
      if (creationResult?.outcome === 'PAYMENT_METHOD_UNAVAILABLE') {
        let latestSettings = jbpPaymentMethodSettings
        try {
          const refreshed = await refetchJbpPaymentSettings()
          latestSettings = refreshed.data?.getJbpPaymentMethodSettings?.data ?? latestSettings
        } catch (error) {
          console.log('failed to refresh JBP settings after unavailable payment method', error)
        }
        loading.stop()

        const unavailableMethod = creationResult.unavailablePaymentMethod ?? setting.method
        const unavailableMethods = Array.from(new Set([...knownUnavailableMethods, unavailableMethod]))
        openJbpOptions(values, unavailableMethods, latestSettings)
        return
      }

      loading.stop()

      const invoice = creationResult?.invoice
      if (!invoice || invoice.state !== 'AWAIT_SETTLEMENT') {
        openJbpOptions(
          values,
          [...knownUnavailableMethods, setting.method],
          jbpPaymentMethodSettings,
          t('c2c:fastTradingUnavailable')
        )
        return
      }

      openModal({
        children: (
          <JbpPaymentCollectionModal
            invoice={invoice}
            onChooseOther={() => openJbpOptions(values, knownUnavailableMethods)}
            onClose={onCloseModalAndResetForm}
            setting={setting}
          />
        ),
        onClose: onCloseModalAndResetForm
      })
    } catch (error) {
      loading.stop()
      console.log('error', error)
      openJbpOptions(
        values,
        [...knownUnavailableMethods, setting.method],
        jbpPaymentMethodSettings,
        t('c2c:fastTradingUnavailable')
      )
    }
  }

  const onSubmit = async (data: BuyValues) => {
    Keyboard.dismiss()
    if (data.dealAmount > MAX_DEAL_AMOUNT) {
      setError('dealAmount', { message: t('c2c:paymentAmountLimitExceeded') })
      return
    }
    // if no kyc, open modal to set kyc
    if (maxKYC !== UserFlags.KYC_1_VERIFIED) {
      openModal({
        children: <SetKycModal onClose={onCloseModalAndResetForm} />,
        onClose: closeModal
      })
      return
    }

    if (preferStandardC2c) {
      startC2cBuyOrder(data)
      return
    }

    const eligibleSettings = JBP_DEPOSITS_ENABLED
      ? getEligibleJbpSettings(jbpPaymentMethodSettings, data.dealAmount, currentVipLevel)
      : []
    if (eligibleSettings.length > 0) {
      openJbpOptions(data)
      return
    }

    startC2cBuyOrder(data)
  }

  function onSelectPaymentMethod() {
    Keyboard.dismiss()
    // if no kyc, open modal to set kyc
    if (maxKYC !== UserFlags.KYC_1_VERIFIED) {
      openModal({
        children: <SetKycModal onClose={onCloseModalAndResetForm} />,
        onClose: closeModal
      })
      return
    }
    if (isHidden) {
      show()
    }
    open()
    setType('buy')
  }

  useFocusEffect(
    useCallback(() => {
      if (maxKYC !== UserFlags.KYC_1_VERIFIED) {
        auth.send({ type: 'REFRESH' })
        refetchUser()
      }
    }, [maxKYC])
  )

  return (
    <View className={'h-full'}>
      <FormScrollView className={'p-4'}>
        <View className={'rounded-lg border border-border-10 bg-background-primary p-4'}>
          <NumberInput
            control={control}
            name={'dealAmount'}
            label={t('orders:paymentAmount')}
            type={'number'}
            autoCorrect={false}
            returnKeyType={'done'}
            keyboardType={'decimal-pad'}
            leftElement={
              <Text className={'ml-3 text-sm text-text-secondary'}>
                {marketPresentation.localFiat.symbol ?? marketPresentation.localFiat.code}
              </Text>
            }
          />
          <View className={'h-2'} />
          <SelectPaymentMethodTrigger type={'buy'} value={selectedPaymentText} onPress={onSelectPaymentMethod} />
          {JBP_DEPOSITS_ENABLED && !jbpPaymentSettingsLoading && (
            <>
              <View className={'h-2'} />
              <JbpVipConditions settings={jbpPaymentMethodSettings} vipLevel={currentVipLevel} />
            </>
          )}
          <View className={'h-2'} />
          <View className={'flex-row justify-between'}>
            <Text className={'text-sm text-text-secondary'}>{t('orders:price')}</Text>
            <Text className={'font-roboto-medium text-sm font-medium text-text-primary'}>
              {formatMarketPrice(BASE_PRICE)}
            </Text>
          </View>
          <View className={'h-4'} />
          <View className={'flex-row justify-between'}>
            <Text className={'text-sm text-text-secondary'}>{t('orders:expectedReceived')}</Text>
            <Text className={'font-roboto-medium text-sm font-medium text-primary'}>
              {formatTokenAmount(shownDealAmount.toString())}
            </Text>
          </View>
          <View className={'h-4'} />
          <Text className={'text-sm font-medium text-text-warning'}>
            {t('c2c:securityReminder', { token: marketPresentation.platformToken.displayName })}
          </Text>
          <View className={'h-3'} />
          <Button
            size={'md'}
            disabled={
              !shownDealAmount ||
              loading.isLoading ||
              (!selectedPayment &&
                (!JBP_DEPOSITS_ENABLED || jbpPaymentSettingsLoading || eligibleJbpSettings.length === 0))
            }
            onPress={handleSubmit(onSubmit)}
          >
            {t('c2c:buyToken', { token: marketPresentation.platformToken.displayName })}
          </Button>
        </View>
      </FormScrollView>
    </View>
  )
}

function ExpressTradingOptionsModal({
  amount,
  failureMessage,
  onClose,
  onUseC2c,
  onUseJbp,
  settings,
  unavailableMethods = [],
  vipLevel
}: {
  amount: number
  failureMessage?: string
  onClose: () => void
  onUseC2c: () => void
  onUseJbp: (setting: JbpPaymentMethodSetting) => void
  settings: JbpPaymentMethodSetting[]
  unavailableMethods?: JbpPaymentMethodSetting['method'][]
  vipLevel: number
}) {
  const { t } = useTranslation()
  const { height } = useWindowDimensions()
  const unavailableMethodLabels = unavailableMethods.map(getJbpPaymentMethodLabel).join('、')
  const hasRetryableUnavailableMethod = unavailableMethods.some((method) =>
    settings.some((setting) => setting.method === method)
  )
  const unavailableMessage = hasRetryableUnavailableMethod
    ? t('c2c:unavailableMethodRetry', { methods: unavailableMethodLabels })
    : t('c2c:unavailableMethodChooseOther', { methods: unavailableMethodLabels })

  return (
    <>
      <ModalHeader className={'p-4'}>
        <ModalTitle>{t('c2c:selectTradingMethod')}</ModalTitle>
      </ModalHeader>
      <ModalBody className={'items-stretch p-0'}>
        <ScrollView style={{ maxHeight: Math.max(160, height - 240) }} contentContainerStyle={{ padding: 16 }}>
          {(unavailableMethods.length > 0 || failureMessage) && (
            <>
              <Banner mood={'error'} title={t('c2c:paymentMethodsUnavailable')}>
                {[failureMessage, unavailableMethods.length > 0 ? unavailableMessage : undefined]
                  .filter(Boolean)
                  .join(' ')}
              </Banner>
              <View className={'h-3'} />
            </>
          )}
          <Text className={'text-sm text-text-secondary'}>
            {t('c2c:vipFastPayEligibility', { level: vipLevel, methodType: t('orders:paymentMethod') })}
          </Text>
          <View className={'h-3'} />
          <View className={'gap-y-2'}>
            {settings.map((setting) => (
              <JbpExpressPaymentOption
                amount={amount}
                key={setting.method}
                setting={setting}
                temporarilyUnavailable={unavailableMethods.includes(setting.method)}
                onPress={() => onUseJbp(setting)}
              />
            ))}
          </View>
        </ScrollView>
      </ModalBody>
      <ModalFooter className={'flex-col items-stretch gap-y-2 p-4'}>
        <Button size={'md'} variant={'outline'} onPress={onUseC2c}>
          {t('c2c:continueStandardC2C')}
        </Button>
        <Button size={'md'} variant={'flat'} onPress={onClose}>
          {t('cancel')}
        </Button>
      </ModalFooter>
    </>
  )
}

function JbpExpressPaymentOption({
  amount,
  onPress,
  setting,
  temporarilyUnavailable = false
}: {
  amount: number
  onPress: () => void
  setting: JbpPaymentMethodSetting
  temporarilyUnavailable?: boolean
}) {
  const { t } = useTranslation()
  const methodLabel = getJbpPaymentMethodLabel(setting.method)

  return (
    <Pressable
      accessibilityLabel={
        temporarilyUnavailable ? t('c2c:methodUnavailableRetry', { method: methodLabel }) : methodLabel
      }
      className={'rounded-lg border border-border-10 bg-background-secondary p-3'}
      onPress={onPress}
    >
      <View className={'flex-row items-center justify-between'}>
        <Text className={'text-sm font-medium text-text-primary'}>{methodLabel}</Text>
        <View className={'flex-row items-center gap-1.5'}>
          {temporarilyUnavailable && <View className={'size-2 rounded-full bg-danger'} />}
          <Text className={temporarilyUnavailable ? 'text-xs text-text-danger' : 'text-xs text-primary'}>
            {temporarilyUnavailable ? t('c2c:unavailableRetry') : t('c2c:selectThisMethod')}
          </Text>
        </View>
      </View>
      <View className={'mt-2 flex-row justify-between'}>
        <Text className={'text-xs text-text-secondary'}>{t('c2c:vipAvailability')}</Text>
        <Text className={'font-roboto-medium text-xs text-text-primary'}>{formatJbpRangeLabel(setting)}</Text>
      </View>
      <View className={'mt-1 flex-row justify-between'}>
        <Text className={'text-xs text-text-secondary'}>{t('orders:paymentAmount')}</Text>
        <Text className={'font-roboto-medium text-xs text-text-primary'}>{formatJbpCurrency(amount)}</Text>
      </View>
      <View className={'mt-1 flex-row justify-between'}>
        <Text className={'text-xs text-text-secondary'}>{t('c2c:fee')}</Text>
        <Text className={'font-roboto-medium text-xs text-text-primary'}>{formatJbpFeeLabel(setting, amount)}</Text>
      </View>
      <View className={'mt-1 flex-row justify-between'}>
        <Text className={'text-xs text-text-secondary'}>{t('c2c:expectedCredit')}</Text>
        <Text className={'font-roboto-medium text-xs text-primary'}>
          {formatTokenAmount(calculateJbpNetCredit(setting, amount).toString())}
        </Text>
      </View>
    </Pressable>
  )
}

function JbpPaymentCollectionModal({
  invoice,
  onChooseOther,
  onClose,
  setting
}: {
  invoice: JbpDepositInvoice
  onChooseOther: () => void
  onClose: () => void
  setting: JbpPaymentMethodSetting
}) {
  const { t } = useTranslation()
  const paymentAmount = invoice.requireAmount ?? invoice.requestedAmount
  const expectedCredit = Math.max(0, Number(paymentAmount) - Number(invoice.transactionFeeAmount ?? 0))
  const { height } = useWindowDimensions()
  const methodLabel = getJbpPaymentMethodLabel(setting.method)

  if (invoice.checkoutUrl) {
    return (
      <JbpCheckout
        checkoutUrl={invoice.checkoutUrl}
        method={setting.method}
        onChooseOther={onChooseOther}
        onClose={onClose}
      />
    )
  }

  return (
    <>
      <ModalHeader className={'p-4'}>
        <ModalTitle>{t('c2c:fastPayCollectionInformation')}</ModalTitle>
      </ModalHeader>
      <ModalBody className={'items-stretch p-0'}>
        <ScrollView style={{ maxHeight: Math.max(160, height - 280) }}>
          <View className={'px-4 pb-3'}>
            <Text className={'text-center text-sm text-text-secondary'}>
              {invoice.qrCodeImageUrl ? t('c2c:scanToPay', { method: methodLabel }) : t('c2c:payUsingInformation')}
            </Text>
          </View>
          {invoice.qrCodeImageUrl && (
            <View className={'items-center px-4 pb-4'}>
              <View className={'rounded-xl border border-border-10 bg-white p-3'}>
                <Image
                  accessibilityLabel={t('c2c:receivingQrCode', { method: methodLabel })}
                  contentFit={'contain'}
                  source={{ uri: invoice.qrCodeImageUrl }}
                  style={{ width: 200, height: 200 }}
                />
              </View>
            </View>
          )}
          <DescriptionListItem label={t('c2c:orderNumber')} canCopy={true} hasBorderTop={true}>
            {invoice.companyOrderNum}
          </DescriptionListItem>
          {invoice.accountName && (
            <DescriptionListItem label={t('c2c:receivingName')} canCopy={true}>
              {invoice.accountName}
            </DescriptionListItem>
          )}
          {invoice.accountNum && (
            <DescriptionListItem label={t('c2c:receivingAccount')} canCopy={true}>
              {invoice.accountNum}
            </DescriptionListItem>
          )}
          {setting.method === 'BANK_CARD' && invoice.bankName && (
            <DescriptionListItem label={t('orders:bankName')}>{invoice.bankName}</DescriptionListItem>
          )}
          {setting.method === 'BANK_CARD' && invoice.bankAddress && (
            <DescriptionListItem label={t('orders:bankBranch')}>{invoice.bankAddress}</DescriptionListItem>
          )}
          <DescriptionListItem label={t('c2c:amountDue')} isValuePrimary={true} canCopy={true}>
            {formatJbpCurrency(paymentAmount)}
          </DescriptionListItem>
          <DescriptionListItem label={t('c2c:transactionChannel')}>{methodLabel}</DescriptionListItem>
          <DescriptionListItem label={t('c2c:fee')}>{formatJbpFeeSnapshotLabel(invoice)}</DescriptionListItem>
          <DescriptionListItem label={t('c2c:expectedCredit')}>
            {formatTokenAmount(expectedCredit.toString())}
          </DescriptionListItem>
          {invoice.timeoutAt && (
            <DescriptionListItem label={t('c2c:paymentDeadline')} hasBorderBottom={true}>
              {formatDate(invoice.timeoutAt)}
            </DescriptionListItem>
          )}
          {!invoice.timeoutAt && (
            <DescriptionListItem label={t('c2c:notice')} hasBorderBottom={true}>
              {t('c2c:payExactAmount')}
            </DescriptionListItem>
          )}
        </ScrollView>
      </ModalBody>
      <ModalFooter className={'flex-col items-stretch gap-y-2 p-4'}>
        <Text className={'text-xs text-text-secondary'}>{t('fastPay:chooseOtherMethodHint')}</Text>
        <Button size={'md'} variant={'outline'} onPress={onChooseOther}>
          {t('fastPay:chooseOtherMethod')}
        </Button>
        <Button size={'md'} onPress={onClose}>
          {t('c2c:close')}
        </Button>
      </ModalFooter>
    </>
  )
}

function SetKycModal({ onClose }: { onClose: () => void }) {
  const router = useRouter()
  const { t } = useTranslation()
  return (
    <>
      <ModalHeader className={'p-4'}>
        <ModalTitle>{t('c2c:kycRequired')}</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <Text className={'text-sm text-text-secondary'}>{t('c2c:kycRequiredDescription')}</Text>
      </ModalBody>
      <ModalFooter>
        <Button className={'flex-1'} size={'md'} variant={'outline'} onPress={onClose}>
          {t('c2c:gotIt')}
        </Button>
        <Button
          className={'flex-1'}
          size={'md'}
          onPress={() => {
            onClose()
            router.push('/setting/kyc')
          }}
        >
          {t('c2c:goToKyc')}
        </Button>
      </ModalFooter>
    </>
  )
}

function TooManyOrdersModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  return (
    <>
      <ModalHeader className={'p-4'}>
        <ModalTitle>{t('c2c:cannotStartTrade')}</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <Text className={'text-center text-sm text-text-secondary'}>{t('c2c:buyOrderInProgress')}</Text>
      </ModalBody>
      <ModalFooter>
        <Button className={'flex-1'} size={'md'} variant={'solid'} onPress={onClose}>
          {t('c2c:gotIt')}
        </Button>
      </ModalFooter>
    </>
  )
}
